/**
 * Slice 6 — Keyboard navigation for the FK Breadcrumb tree.
 *
 * Registers a window-level `keydown` listener while the hook is mounted
 * (i.e. while the breadcrumb chip-row is rendered, which itself is only
 * rendered when a navigationTree exists and has depth > 1). On `⌘← ⌘→
 * ⌘↑ ⌘↓` it resolves the next active node via `resolveKeyboardTarget`,
 * fires `feature:fk_nav_keyboard` analytics, and dispatches activation.
 *
 * Guards:
 *  - No-op when typing inside an input / textarea / contenteditable — so
 *    arrow keys in the cell editor and filter wizard are unaffected.
 *  - Listener uses `capture: true` and stops propagation so Monaco / grid
 *    don't also see the ⌘ + arrow when this tab owns the focus.
 */

import { useLayoutEffect, useRef } from 'react';
import { useAnalytics } from '../../../../../core/analytics/useAnalytics';
import { resolveKeyboardTarget, type BreadcrumbKey } from './breadcrumbTreeQueries';
import type {
  NavigationNodeId,
  NavigationTree,
} from '../../../core/types/workspaceTypes';
import { isTableExplorerTab } from '../../../core/types/workspaceTypes';
import { useWorkspaceStore } from '../../../core/store/workspaceStore';
import {
  registerKeybindingTarget,
  type KeybindingHandlersRef,
} from '../../../../keybindings/dispatch/targetRegistry';

type UseBreadcrumbKeyboardArgs = {
  readonly tree: NavigationTree | null;
  readonly onActivate: (id: NavigationNodeId) => void;
};

export const BREADCRUMB_KEYBINDING_COMMAND_IDS = [
  'breadcrumb.previousSibling',
  'breadcrumb.nextSibling',
  'breadcrumb.parent',
  'breadcrumb.firstChild',
] as const;

function shouldIgnoreTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  // Monaco renders its widgets inside `.monaco-editor` — ignore those too.
  if (target.closest('.monaco-editor')) return true;
  return false;
}

export function useBreadcrumbKeyboard({ tree, onActivate }: UseBreadcrumbKeyboardArgs): void {
  const { track } = useAnalytics();
  const tabId = useWorkspaceStore((state) => state.tabs.find((tab) =>
    isTableExplorerTab(tab) && tab.content.navigationTree === tree
  )?.id ?? null);
  const handlersRef = useRef<KeybindingHandlersRef>({ current: {} });

  const activate = (key: BreadcrumbKey): boolean => {
    const targetId = tree ? resolveKeyboardTarget(tree, key) : null;
    if (!targetId) return false;
    track('feature:fk_nav_keyboard', { key });
    onActivate(targetId);
    return true;
  };
  handlersRef.current.current = {
    'breadcrumb.previousSibling': () => activate('left'),
    'breadcrumb.nextSibling': () => activate('right'),
    'breadcrumb.parent': () => activate('up'),
    'breadcrumb.firstChild': () => activate('down'),
  };

  useLayoutEffect(() => {
    if (!tree || !tabId) return undefined;
    const getBreadcrumb = () => Array.from(
      document.querySelectorAll<HTMLElement>('[data-testid="fk-breadcrumb"]'),
    ).find((element) => element.dataset.tabId === tabId) ?? null;
    return registerKeybindingTarget({
      scope: 'breadcrumb',
      tabId,
      handlers: handlersRef.current,
      getRootElement: () => getBreadcrumb()?.parentElement?.parentElement ?? getBreadcrumb(),
      canExecute: (commandId) => {
        if (shouldIgnoreTarget(document.activeElement)) return false;
        const keyByCommand = {
          'breadcrumb.previousSibling': 'left',
          'breadcrumb.nextSibling': 'right',
          'breadcrumb.parent': 'up',
          'breadcrumb.firstChild': 'down',
        } as const;
        const key = keyByCommand[commandId as keyof typeof keyByCommand];
        return key ? resolveKeyboardTarget(tree, key) !== null : false;
      },
    });
  }, [tabId, tree]);
}
